import React, { FC, ReactNode } from "react";
import { trackWithDefaultStage } from "@flights/et-universal";
import { Text, useViewport } from "@bookingcom/bui-react";
import { I18nChildContext, useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";

import useUserAgent from "../../../hooks/useUserAgent";
import useRouteName from "../../../hooks/useRouteName";
import Frame from "../Frame";
import styles from "./FlightDetailsAncillaryItemLayout.module.css";
import flights_web_condensed_extras_fd_mdot from "../../../utils/experiments/funnel/flights_web_condensed_extras_fd_mdot";
import web_flights_fd_genius_saving_badge_percentage from "../../../utils/experiments/genius/web_flights_fd_genius_saving_badge_percentage";
import useProfiler from "../../../hooks/useProfiler";

const FlightDetailsAncillaryItemLayout: FC<{
  icon: ReactNode;
  recommendation?: ReactNode;
  content: ReactNode;
  includedBaggage?: boolean;
  hideIncludedSection?: boolean;
  alignIconCenter?: boolean;
  extraTestId?: string;
  condenseExtras?: boolean;
  geniusSavingIncluded?: boolean;
}> = ({
  icon,
  recommendation,
  content,
  includedBaggage,
  hideIncludedSection = false,
  alignIconCenter = false,
  extraTestId,
  condenseExtras = false,
  geniusSavingIncluded = false
}) => {
  const { isMobile } = useUserAgent();
  const { isLoggedIn } = useProfiler();
  const i18n = useI18n() as I18nChildContext;
  const routeName = useRouteName();
  const { isSmall } = useViewport();

  const showAvailableNextLabel = routeName.includes("flightdetails");
  const condensedBaggageIncluded = isMobile && !!flights_web_condensed_extras_fd_mdot.trackWithDefaultStage();

  const isResponsiveFixesSm = isSmall && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);

  const isInGeniusSavingBadgeExperiment =
    isLoggedIn() && web_flights_fd_genius_saving_badge_percentage.trackWithDefaultStage();
  const includedText =
    isInGeniusSavingBadgeExperiment && geniusSavingIncluded
      ? i18n.trans(t("flights_fd_campaign_applied_label"))
      : i18n.trans(t("flights_ancillary_included"));

  return (
    <div>
      <Frame
        direction="row"
        alignItems={alignIconCenter ? "center" : undefined}
        attributes={{ "data-testid": `flight_details_ancillary_item_layout_${extraTestId}` }}
      >
        <div className={styles.iconWrapper}>{icon}</div>
        <Frame
          direction={
            isResponsiveFixesSm || (isMobile && !flights_web_condensed_extras_fd_mdot.trackWithDefaultStage())
              ? "column"
              : "row"
          }
          alignItems={
            isResponsiveFixesSm
              ? "start"
              : isMobile && !flights_web_condensed_extras_fd_mdot.trackWithDefaultStage()
              ? undefined
              : "center"
          }
          className={styles.mainBlock}
        >
          <div className={styles.content} data-testid="flight_details_ancillary_item_layout_content">
            {content}
          </div>
          {hideIncludedSection ? null : !condensedBaggageIncluded && includedBaggage ? (
            <Text className={styles.included} attributes={{ "data-testid": "baggage_status" }} variant="body_2">
              {includedText}
            </Text>
          ) : condensedBaggageIncluded ? null : (
            <Frame alignItems={!isMobile ? "flex-end" : undefined}>
              {recommendation && (
                <Frame pb={1} pt={1}>
                  {recommendation}
                </Frame>
              )}
              <Text variant="body_2">
                {showAvailableNextLabel
                  ? i18n.trans(t("flights_ancillary_available_next"))
                  : i18n.trans(t("flights_ancillary_not_included"))}
              </Text>
            </Frame>
          )}
        </Frame>
        <Frame justifyContent={condenseExtras ? "center" : undefined}>
          {condensedBaggageIncluded && includedBaggage ? (
            <Text className={styles.included} attributes={{ "data-testid": "baggage_status" }} variant="small_1">
              {includedText}
            </Text>
          ) : null}
        </Frame>
      </Frame>
    </div>
  );
};

export default FlightDetailsAncillaryItemLayout;
